import {
  IClassesDataEntity,
  IGradesDataEntity,
  IStudentsDataEntity,
  ITeachersDataEntity,
} from "app/services/reports";
import { IDomainFormativeData } from "features/Reports";

export interface IFormativeEntity {
  totalAssigned: number;
  totalCompleted: number;
  avgScore: number;
}
export interface ITeacherFormativeData extends IFormativeEntity {
  teacherCode: string;
  teacherFirstName: string;
  teacherLastName: string;
}
export interface ITeachersDomainFormativeData extends IDomainFormativeData {
  teachers: ITeacherFormativeData[];
}

export interface IClassFormativeData {
  totalCompleted: number;
  avgScore: number;
  classCode: string;
  gradeCode: string;
  gradeTitle: string;
  totalAssigned: number;
  classTitle: string;
}
export interface ITeacherDomainFormativeData extends IDomainFormativeData {
  classes: IClassFormativeData[];
}

export interface IGradeFormativeData extends IFormativeEntity {
  gradeCode: string;
  gradeTitle: string;
}
export interface IGradesDomainFormativeData extends IDomainFormativeData {
  grades: IGradeFormativeData[];
}

export interface IStudentFormativeData extends IFormativeEntity {
  studentCode: number;
  studentFirstName: string;
  studentLastName: string;
}
export interface IStudentsDomainFormativeData extends IDomainFormativeData {
  students: IStudentFormativeData[];
}

export const transformTeachersFormativeData = (
  data: ITeachersDataEntity[]
): ITeachersDomainFormativeData[] =>
  data.map((item) => ({
    domainLanguageTitle: item._domainLanguageCode,
    domainLanguageCode: item._domainLanguageCode,
    totalAssignedStudents: item._totalAssignedLosForDomain,
    submittedAssessments: item._totalSubmittedOfflineAssessmentsForDomain,
    avgCompletedStudents: item._totalCompletedLosForDomain,
    evaluatedAssessments: item._totalEvaluatedOfflineAssessmentsForDomain,
    avgScore: item._averageDomainScore,
    teachers: item._teachers.map((teacher) => ({
      totalCompleted: teacher._totalCompletedLos,
      avgScore: teacher._avgScore,
      teacherCode: teacher._teacherId,
      teacherFirstName: teacher._teacherName.split(" ")[0],
      teacherLastName: teacher._teacherName.split(" ").slice(1).join(" "),
      totalAssigned: teacher._totalAssignedLos,
    })),
  }));

export const transformClassesFormativeData = (
  data: IClassesDataEntity[]
): ITeacherDomainFormativeData[] =>
  data.map((item) => ({
    domainLanguageTitle: item._domainLanguageCode,
    domainLanguageCode: item._domainLanguageCode,
    totalAssignedStudents: item._totalAssignedLosForDomain,
    submittedAssessments: item._totalSubmittedOfflineAssessmentsForDomain,
    avgCompletedStudents: item._totalCompletedLosForDomain,
    evaluatedAssessments: item._totalEvaluatedOfflineAssessmentsForDomain,
    avgScore: item._averageDomainScore,
    classes: item._classes.map((cl) => ({
      totalCompleted: cl._totalCompletedLos,
      avgScore: cl._avgScore,
      classCode: cl._classId,
      gradeCode: cl._gradeCode,
      gradeTitle: cl._gradeTitle,
      totalAssigned: cl._totalAssignedLos,
      classTitle: cl._className,
    })),
  }));

export const transformGradesFormativeData = (
  data: IGradesDataEntity[]
): IGradesDomainFormativeData[] =>
  data.map((item) => ({
    domainLanguageTitle: item._domainLanguageCode,
    domainLanguageCode: item._domainLanguageCode,
    totalAssignedStudents: item._totalAssignedLosForDomain,
    submittedAssessments: item._totalSubmittedOfflineAssessmentsForDomain,
    avgCompletedStudents: item._totalCompletedLosForDomain,
    evaluatedAssessments: item._totalEvaluatedOfflineAssessmentsForDomain,
    avgScore: item._averageDomainScore,
    grades: item._grades.map((grade) => ({
      totalCompleted: grade._totalCompletedLos,
      avgScore: grade._avgScore,
      gradeCode: grade._gradeCode,
      gradeTitle: grade._gradeTitle ?? "",
      totalAssigned: grade._totalAssignedLos,
    })),
  }));

export const transformStudentsFormativeReport = (
  data: IStudentsDataEntity[]
): IStudentsDomainFormativeData[] =>
  data.map((item) => ({
    domainLanguageTitle: item._domainLanguageCode,
    domainLanguageCode: item._domainLanguageCode,
    totalAssignedStudents: item._totalAssignedLosForDomain,
    submittedAssessments: item._totalSubmittedOfflineAssessmentsForDomain,
    avgCompletedStudents: item._totalCompletedLosForDomain,
    evaluatedAssessments: item._totalEvaluatedOfflineAssessmentsForDomain,
    avgScore: item._averageDomainScore,
    students: item._students.map((stu) => ({
      totalCompleted: stu._totalCompletedLos,
      avgScore: stu._avgScore,
      studentCode: stu._studentId,
      studentFirstName: stu._studentName.split(" ")[0],
      studentLastName: stu._studentName.split(" ").slice(1).join(" "),
      totalAssigned: stu._totalAssignedLos,
    })),
  }));
